import type { ContextBudget, ProjectContext } from './context-types.js';
import { detectProjectInfo } from './project-detector.js';
import { loadIgnoreRules, createIgnoreMatcher } from './ignore-rules.js';
import { scanProjectStructure } from './structure-scanner.js';
import { readGitStatus } from './git-reader.js';
import { readImportantFiles } from './important-files.js';

export interface BuildProjectContextOptions {
  maxChars?: number;
  maxDepth?: number;
  maxFiles?: number;
}

function applyBudget(markdown: string, maxChars: number): { markdown: string; budget: ContextBudget } {
  const truncated = markdown.length > maxChars;
  const output = truncated
    ? `${markdown.slice(0, maxChars)}\n\n...[project context truncated]`
    : markdown;
  return { markdown: output, budget: { maxChars, usedChars: output.length, truncated } };
}

export function formatProjectContext(context: Omit<ProjectContext, 'markdown' | 'budget'>): string {
  const lines: string[] = [];
  lines.push('# Project Intelligence');
  lines.push(`- Root: ${context.info.root}`);
  lines.push(`- Type: ${context.info.type}`);
  lines.push(`- Package manager: ${context.info.packageManager}`);
  lines.push(`- Languages: ${context.info.languages.join(', ') || 'unknown'}`);
  lines.push(`- Frameworks: ${context.info.frameworks.join(', ') || 'none detected'}`);
  lines.push(`- Monorepo: ${context.info.isMonorepo ? 'yes' : 'no'}`);
  lines.push(`- Git: ${context.git.available ? `branch ${context.git.branch ?? 'unknown'}, ${context.git.entries.length} changes` : 'unavailable'}`);
  lines.push('');
  lines.push('## Structure');
  for (const dir of context.structure.directories.slice(0, 50)) lines.push(`- dir ${dir}`);
  for (const file of context.structure.files.slice(0, 80)) lines.push(`- file ${file}`);
  if (context.structure.truncated) lines.push('- ...structure truncated');
  lines.push('');
  lines.push('## Important Files');
  for (const file of context.importantFiles) {
    lines.push(`### ${file.path}`);
    lines.push('```');
    lines.push(file.content);
    lines.push('```');
  }
  return lines.join('\n');
}

export async function buildProjectContext(
  workspaceRoot: string,
  options: BuildProjectContextOptions = {},
): Promise<ProjectContext> {
  const maxChars = options.maxChars ?? 15_000;
  const ignoreRules = await loadIgnoreRules(workspaceRoot);
  const ignore = createIgnoreMatcher(ignoreRules);
  const [info, structure, git] = await Promise.all([
    detectProjectInfo(workspaceRoot),
    scanProjectStructure(workspaceRoot, {
      ignore,
      maxDepth: options.maxDepth ?? 4,
      maxFiles: options.maxFiles ?? 100,
    }),
    readGitStatus(workspaceRoot),
  ]);
  const importantFiles = await readImportantFiles(workspaceRoot, structure.files);
  const base = { info, structure, git, importantFiles };
  const { markdown, budget } = applyBudget(formatProjectContext(base), maxChars);
  return { ...base, budget, markdown };
}
